import { defineStore } from 'pinia'
import { EMAIL_REGEX } from '@/constants/customer'
import { packageDealPossible } from '@/composables/usePackageDeal.js'
const env = import.meta.env.VITE_WEB_ENV

export const useCustomerStore = defineStore('customer', {
  state: () => ({
    customer: {},
    customerAddress: null,
    customerOrderId: null,
    canEditCountryOfCurrentOrder: true,
    isCustomerProfileEdit: false,
    guestAccount: {},
    isCustomerSelected: false,
    isOrderOverviewAddress: false,
    orderId: '',
    whishListSkuCodes: [],
    wishlist: [] // <-- Add wishlist to state
  }),
  getters: {
    getCustomerId: (state) => state.customer.id,
    getCustomerSupabaseId: (state) => state.customer.supabase_userId,
    getCustomerAddress: (state) => state.customerAddress,
    getCustomer: (state) => state.customer,
    getCustomerOrderId: (state) => state.customerOrderId,
    getCurrentCart: (state) => state.customer.currentCart,
    getGuestAccount: (state) => state.guestAccount,
    getLastOrder: (state) => state.customer.lastOrder,
    hasInvalidEmail(state) {
      return Boolean(state.customer?.email && !EMAIL_REGEX.test(state.customer.email))
    }
  },
  actions: {
    setCustomer(customer) {
      // reset the customer store to the initial state
      this.reset()
      this.customer = customer
    },
    setOrders(orders) {
      this.customer.orders = orders
    },
    setCarts(carts) {
      this.customer.carts = carts
    },
    setCurrentCart(currentCart) {
      this.customer.currentCart = currentCart
      if (currentCart?.market?.metadata?.country?.toUpperCase() === 'DE') {
        // see CL shipping method Europe -> Zone
        this.customer.currentCart.shippingCostZone = 'EUROPE'
      }

      if (this.customer.currentCart?.status) {
        const cartIsBlocked = ['placed', 'editing'].includes(this.customer?.currentCart?.status)
        const paymentInProgress =
          cartIsBlocked && !['free', 'paid'].includes(this.customer?.currentCart?.payment_status)

        if (paymentInProgress) {
          this.customer.currentCart.isPaymentInProgress = true
        } else if (cartIsBlocked) {
          this.customer.currentCart.isBlocked = true
        }
      }

      // check if last order is set
      if (this.customer.currentCart) {
        this.customer.currentCart.isPackageDealPossible = packageDealPossible(
          this.customer.currentCart,
          this.customer.lastOrder
        )
      }
    },
    setCustomerAddress(address) {
      this.customerAddress = address
    },
    setCustomerOrderId(id) {
      this.customerOrderId = id
    },
    setGuestAccount(account) {
      this.guestAccount = account
    },
    setCustomerValidationErrors(key, errors) {
      if (!this.customer.validationErrors) {
        this.customer.validationErrors = {}
      }
      this.customer.validationErrors[key] = errors
    },
    setCartValidationErrors(errors) {
      if (!this.customer.currentCart || errors?.length === 0) {
        return
      }
      this.customer.currentCart.validationErrors = errors
      this.customer.currentCart.validationHasErrors = true
    },
    setCustomerLastOrder(order) {
      this.customer.lastOrder = order

      if (this.customer.currentCart) {
        this.customer.currentCart.isPackageDealPossible = packageDealPossible(
          this.customer.currentCart,
          order
        )
      }
    },
    resetValidationErrors() {
      if (this.customer?.currentCart && this.customer.currentCart?.validationErrors) {
        this.customer.currentCart.validationErrors = []
        this.customer.currentCart.validationHasErrors = false
      }

      if (this.customer?.validationErrors) {
        delete this.customer.validationErrors
      }
    },
    addCartLineItem(lineItem) {
      this.customer.currentCart.line_items.push(lineItem)
    },
    removeAddressById(id) {
      const indexToRemove = this.customer.customer_addresses.findIndex((a) => a.id === id)
      this.customer.customer_addresses.splice(indexToRemove, 1)
    },
    updateWhishListSkuCodes(skuCodes) {
      this.whishListSkuCodes = [...skuCodes]
    },
    isInWhishList(skuCode) {
      return this.whishListSkuCodes.includes(skuCode)
    },
    addToWishlist(sku) {
      if (!this.wishlist.includes(sku)) {
        this.wishlist.push(sku)
        // Persist to localStorage or API
      }
    },
    removeFromWishlist(sku) {
      const index = this.wishlist.indexOf(sku)
      if (index > -1) {
        this.wishlist.splice(index, 1)
        // Persist to localStorage or API
      }
    },
    reset() {
      // reset all values to the initial state
      this.customer = {}
      this.customerAddress = null
      this.customerOrderId = null
      this.canEditCountryOfCurrentOrder = true
      this.isCustomerProfileEdit = false
      this.guestAccount = {}
      this.isCustomerSelected = false
      this.isOrderOverviewAddress = false
      this.orderId = ''
      this.whishListSkuCodes = []
      this.wishlist = [] // <-- Reset wishlist
    }
  }
})
